<script lang="ts" setup>
import { computed, reactive, ref, watch } from 'vue'

import { useStore } from '@/store'
import {
  ConnectorFlow,
  ConnectorFlowType,
  connectorFlowVersions,
  connectorsConnexionInputs,
  ConnectorType,
  fileConnectors
} from '@/store/modules/connector-config/types'
import { pmsiTypesLabels } from '@/store/modules/settings/types'

export interface ConnectorFlowFormProps {
  index: number
  connectorIndex: number
  connectorType: ConnectorType
  flow: ConnectorFlow
}

export interface ConnectorFlowEmits {
  'update-flow': [index: number, flowData: ConnectorFlow]
  'delete-flow': [index: number]
}

const emit = defineEmits<ConnectorFlowEmits>()

const store = useStore()
const props = defineProps<ConnectorFlowFormProps>()

const flowData = reactive({ ...props.flow })
const showDeleteDropdown = ref(false)
const isTestingWriteRight = ref(false)
const pathIsWritable = ref(null)

const availableVersions = computed(() => connectorFlowVersions[props.connectorType] ?? [])

watch(availableVersions, (versions) => {
  if (versions.length === 1 && !versions.includes(flowData.formatVersion)) {
    flowData.formatVersion = versions[0]
  }
}, { immediate: true })

watch(flowData, function() {
  emit('update-flow', props.index, flowData)
})

function testWriteRight() {
  isTestingWriteRight.value = true
  pathIsWritable.value = null // restore value to null so we don't display any message

  store.dispatch('connectorConfig/testWriteRight', flowData.path).then((response) => {
    isTestingWriteRight.value = false
    pathIsWritable.value = response['writable']
  })
}

function deleteFlow() {
  showDeleteDropdown.value = false
  emit('delete-flow', props.index)
}

</script>

<template>
  <div
    class="col-12 mb-4 p-3 shadow-sm d-grid gap-2"
    style="border-left: 3px solid;"
  >
    <div class="form-group row ">
      <label class="col-sm-2">Type</label>
      <div class="col-sm-10">
        <select
          v-model="flowData.type"
          class="form-select py-2"
        >
          <option :value="null">
            --- Choisissez un type ---
          </option>
          <option
            v-for="item in ConnectorFlowType"
            :key="item"
            :value="item"
          >
            {{ item }}
          </option>
        </select>
      </div>
    </div>
    <div class="form-group row">
      <label class="col-sm-2">Chemin</label>
      <div class="col-sm-10">
        <input
          v-model="flowData.path"
          type="text"
          class="form-control"
        >
        <div
          v-if="fileConnectors.includes(connectorType)"
          class="d-flex justify-content-between align-items-center"
        >
          <button
            v-if="!isTestingWriteRight"
            type="button"
            class="btn btn-link btn-sm"
            @click="testWriteRight"
          >
            Tester les droits d'écriture
          </button>
          <div
            v-if="isTestingWriteRight"
            class="loader loader-sm loader-center mx-0 my-1"
            style="width: 18px; height: 18px;"
          />
          <small
            v-if="pathIsWritable === true"
            class="text-success"
          >
            Vous avez les droits d'écriture !
          </small>
          <small
            v-if="pathIsWritable === false"
            class="text-danger"
          >
            Vous n'avez pas les droits d'écriture :/
          </small>
        </div>
      </div>
    </div>
    <div class="form-group row">
      <label class="col-sm-2">Schéma</label>
      <div class="col-sm-10">
        <select
          v-model="flowData.formatVersion"
          class="form-select"
        >
          <option
            v-for="item in availableVersions"
            :key="item"
            :value="item"
          >
            {{ item }}
          </option>
        </select>
      </div>
    </div>
    <div
      v-if="connectorsConnexionInputs['pmsiType'].includes(connectorType)"
      class="form-group row col"
    >
      <label class="col-sm-2">Type de PMSI</label>
      <div class="col-sm-10">
        <select
          v-model="flowData.pmsiType"
          class="form-select"
        >
          <option
            :value="null"
          >
            --- Choisissez un type ---
          </option>
          <option
            v-for="(label, value) in pmsiTypesLabels"
            :key="value"
            :value="value"
          >
            {{ label }}
          </option>
        </select>
      </div>
    </div>
    <div class="form-group d-flex justify-content-start">
      <div>
        <v-dropdown
          placement="top"
          :shown="showDeleteDropdown"
          @hide="!showDeleteDropdown"
        >
          <button
            type="button"
            class="btn btn-outline-danger btn-sm"
            @click="showDeleteDropdown = true"
          >
            Supprimer le flux
          </button>
          <template #popper>
            <div class="p-3">
              <h4 class="title-light text-center">
                Êtes-vous sûr ?
              </h4>
              <div class="text-center">
                <button
                  class="btn btn-danger btn-padded"
                  @click="deleteFlow()"
                >
                  Supprimer
                </button>
              </div>
            </div>
          </template>
        </v-dropdown>
      </div>
    </div>
  </div>
</template>
